import React, { useState, useRef } from 'react';
import {
  TextField,
  Box,
  Typography,
  IconButton,
  Chip,
  Divider
} from '@mui/material';
import {
  Percent as PercentIcon,
} from '@mui/icons-material';
import { useTheme } from '../../contexts/ThemeContext';

const CostPriceInput = ({ type, isWarning, ...props }) => {
  const { theme } = useTheme();
  const [isPercentageMode, setIsPercentageMode] = useState(false);
  const [baseValue, setBaseValue] = useState('');
  const [percentageValue, setPercentageValue] = useState('');
  const [operation, setOperation] = useState('subtract'); // 'add', 'subtract'
  const inputRef = useRef(null);

  const calculateResult = () => {
    const base = parseFloat(baseValue);
    const percent = parseFloat(percentageValue);
    
    if (!isNaN(base) && !isNaN(percent)) {
      let result;
      switch (operation) {
        case 'add':
          result = base + (base * (percent / 100));
          break;
        case 'subtract':
          result = base - (base * (percent / 100));
          break;
        default:
          result = 0;
      }
      return result.toFixed(2);
    }
    return '';
  };

  const applyCalculation = () => {
    const result = calculateResult();
    if (result && props.onChange) {
      props.onChange({ target: { value: result } });
      // Reset percentage mode
      setIsPercentageMode(false);
      setBaseValue('');
      setPercentageValue('');
    }
  };

  const handlePercentageToggle = () => {
    if (isPercentageMode) {
      // Exit percentage mode
      setIsPercentageMode(false);
      setBaseValue('');
      setPercentageValue('');
    } else {
      // Enter percentage mode
      setIsPercentageMode(true);
      setBaseValue(props.value || '');
    }
  };

  const getOperationSymbol = () => {
    switch (operation) {
      case 'add': return '+';
      case 'subtract': return '-';
      default: return '-';
    }
  };

  const cycleOperation = () => {
    const operations = ['add', 'subtract'];
    const currentIndex = operations.indexOf(operation);
    const nextIndex = (currentIndex + 1) % operations.length;
    setOperation(operations[nextIndex]);
  };

  const renderPercentageInput = () => {
    if (!isPercentageMode) return null;

    return (
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'flex-end', 
        gap: 0.5,
        backgroundColor: 'rgba(255, 255, 255, 0.1)',
        borderRadius: 1,
        p: 0.5,
        border: `1px solid ${theme.accent.warning}20`,
        mr: 'auto'
      }}>
        
        <Chip
          label={getOperationSymbol()}
          size="small"
          onClick={cycleOperation}
          sx={{
            height: '20px',
            fontSize: '0.65rem',
            fontFamily: theme.fontFamily,
            backgroundColor: theme.accent.warning,
            color: 'white',
            cursor: 'pointer',
            '&:hover': {
              backgroundColor: theme.accent.warning,
              opacity: 0.8
            }
          }}
        />
        
        <input
          type="number"
          value={percentageValue}
          onChange={(e) => setPercentageValue(e.target.value)}
          placeholder="%"
          style={{
            width: '50px',
            border: 'none',
            outline: 'none',
            fontSize: '0.75rem',
            fontFamily: theme.fontFamily,
            backgroundColor: 'transparent',
            color: theme.text.primary,
            textAlign: 'left'
          }}
        />
        
        <Typography sx={{ 
          fontSize: '0.75rem', 
          color: theme.text.primary,
          fontFamily: theme.fontFamily
        }}>
          %
        </Typography>
        
        <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
        
        <Typography sx={{ 
          fontSize: '0.7rem', 
          color: theme.accent.warning,
          fontFamily: theme.fontFamily,
          fontWeight: 600,
          minWidth: '35px',
          textAlign: 'center'
        }}>
          ={calculateResult()}
        </Typography>
        
        <IconButton
          size="small"
          onClick={applyCalculation}
          disabled={!calculateResult()}
          sx={{
            width: '18px',
            height: '18px',
            color: theme.accent.warning,
            '&:disabled': {
              color: theme.text.secondary
            }
          }}
        >
          ✓
        </IconButton>
      </Box>
    );
  };

  return (
    <Box>
      <TextField
        ref={inputRef}
        sx={{
          "& .MuiOutlinedInput-root": {
            "& fieldset": {
              border: isWarning ? `2px solid ${theme.accent.warning}` : `1px solid ${theme.text.primary}`
            },
            "&:hover fieldset": {
              border: isWarning ? `2px solid ${theme.accent.warning}` : `1px solid ${theme.text.primary}`
            },
            "&.Mui-focused fieldset": {
              border: isWarning ? `2px solid ${theme.accent.warning}` : `1px solid ${theme.text.primary}`
            },
          },
          "& .MuiInputLabel-root": {
            color: isWarning ? theme.accent.warning : theme.text.primary,
            fontFamily: theme.fontFamily,
            "&.Mui-focused": {
              color: isWarning ? theme.accent.warning : theme.text.primary,
            },
          },
          "& .MuiOutlinedInput-input": {
            fontFamily: theme.fontFamily,
            color: theme.text.primary,
          },
        }}
        {...props}
        label={<Box sx={{ mr: "8px" }}>{isWarning ? `${props.label} - optional` : props.label}</Box>}
        type={type}
        slotProps={{
          input: {
            ...props.InputProps,
            endAdornment: (
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 0.5 }}>
                {renderPercentageInput()}
                <IconButton
                  onClick={handlePercentageToggle}
                  edge="end"
                  sx={{
                    color: isPercentageMode ? theme.accent.warning : theme.text.secondary,
                    backgroundColor: isPercentageMode ? `${theme.accent.warning}20` : 'transparent',
                    '&:hover': {
                      backgroundColor: `${theme.accent.warning}30`,
                    }
                  }}
                  size="small"
                >
                  <PercentIcon sx={{ fontSize: '1rem' }} />
                </IconButton>
                {props.InputProps?.endAdornment}
              </Box>
            )
          },
        }}
      />
    </Box>
  );
};

export default CostPriceInput;